import { useState } from "react";

interface UseLocalStorageObjectOptions<T, TStored = T> {
  serialize?: (value: T) => TStored;
  deserialize?: (value: unknown, defaultValue: T) => T;
}

export function useLocalStorageObject<T, TStored = T>(
  key: string,
  defaultValue: T,
  options?: UseLocalStorageObjectOptions<T, TStored>,
) {
  const [state, setState] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key);
      if (!saved) return defaultValue;

      const parsed = JSON.parse(saved) as unknown;
      const deserialized = options?.deserialize
        ? options.deserialize(parsed, defaultValue)
        : (parsed as T);
      return deserialized;
    } catch (err) {
      console.error(`Invalid localStorage data for ${key}`, err);
      return defaultValue;
    }
  });

  const setLocalStorageValue = (value: T | ((prev: T) => T)) => {
    if (typeof value === "function") {
      setState((prev) => {
        const newVal = (value as (prev: T) => T)(prev);
        const persistedValue = options?.serialize
          ? options.serialize(newVal)
          : newVal;
        localStorage.setItem(key, JSON.stringify(persistedValue));
        return newVal;
      });
    } else {
      setState(value);
      const persistedValue = options?.serialize
        ? options.serialize(value)
        : value;
      localStorage.setItem(key, JSON.stringify(persistedValue));
    }
  };

  return [state, setLocalStorageValue] as const;
}
